import React from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useStageFeatureMatrix } from "../../hooks/useStageFeatureMatrix";
import {
  ALC_ACCENT_TEXT,
  ALC_DOT,
  categoryGradientStyle,
  MATRIX_HEADER_CELL,
} from "../../config/accentStyles";
import StageHeaderCell from "../StageHeaderCell/StageHeaderCell";
import OfferingRow from "../OfferingRow/OfferingRow";

const GRID_TEMPLATE = "minmax(300px,1.5fr) repeat(4,1fr) 0.9fr";

const Legend: React.FC = () => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-500 dark:text-slate-400">
    <span className="flex items-center gap-2">
      <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[#34D399]/50 bg-[#34D399]/15 text-[11px] font-bold text-[#0e9f6e] dark:text-[#34D399]">
        +
      </span>
      New at this stage
    </span>
    <span className="flex items-center gap-2">
      <span className="h-2 w-2 rounded-full bg-gray-300 dark:bg-slate-600" />
      Carries forward
    </span>
    <span className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${ALC_DOT}`} />
      Available a la carte
    </span>
  </div>
);

const StageFeatureMatrix: React.FC = () => {
  const { catalog, columns, currentStagePosition } = useStageFeatureMatrix();

  return (
    <MainPageWrapper
      fullWidth
      subtitle="Everything CertainPath offers, side by side across the four membership stages."
      title="Growth Stages"
    >
      <div className="space-y-6 py-4">
        <Legend />

        {/*
          The horizontal-scroll wrapper is also a vertical scroll container
          (overflow-x: auto forces overflow-y to compute to auto), which would
          trap the sticky header. Above 1340px the 960px grid fits beside the
          nav chrome, so the wrapper drops out of scroll-container duty and
          sticky resolves against the page instead.
        */}
        <div className="overflow-x-auto min-[1340px]:overflow-x-visible rounded-lg border border-gray-200 dark:border-slate-700">
          <div
            className="grid min-w-[960px] bg-white dark:bg-slate-800"
            style={{ gridTemplateColumns: GRID_TEMPLATE }}
          >
            {/* Header row */}
            <div
              className={`flex flex-col justify-end px-4 py-5 bg-white dark:bg-slate-800 ${MATRIX_HEADER_CELL}`}
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500 dark:text-slate-400">
                All Four Stages
              </span>
              <span className="mt-1 text-lg font-bold text-gray-900 dark:text-slate-100">
                Everything you unlock, side by side
              </span>
            </div>
            {columns.map((column) => (
              <StageHeaderCell
                column={column}
                isCurrent={currentStagePosition === column.position}
                isLocked={
                  currentStagePosition !== null &&
                  column.position > currentStagePosition
                }
                key={column.position}
              />
            ))}
            <div
              className={`flex flex-col items-center gap-1 px-3 py-5 text-center border-l border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 ${MATRIX_HEADER_CELL}`}
            >
              <span aria-hidden="true" className="mb-1 h-[22px]" />
              <span aria-hidden="true" className="h-[26px]" />
              <span className={`text-[17px] font-bold ${ALC_ACCENT_TEXT}`}>
                A la Carte
              </span>
            </div>

            {/* Category sections */}
            {catalog.map((category) => (
              <React.Fragment key={category.name}>
                <div
                  className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-3 border-y border-gray-200 dark:bg-slate-900 dark:border-slate-700"
                  style={{
                    gridColumn: "1 / -1",
                    ...categoryGradientStyle(category.accent),
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="h-[11px] w-[11px] shrink-0 rounded-[3px]"
                      style={{ backgroundColor: category.accent }}
                    />
                    <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-gray-900 dark:text-white">
                      {category.name}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-slate-300">
                      {category.tagline}
                    </span>
                  </div>
                  <span className="shrink-0 text-[11px] font-semibold text-gray-500 dark:text-slate-400">
                    {category.offerings.length} offerings
                  </span>
                </div>

                {category.offerings.map((offering) => (
                  <OfferingRow
                    columns={columns}
                    currentStagePosition={currentStagePosition}
                    key={offering.name}
                    offering={offering}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
};

export default StageFeatureMatrix;
